import { requestTranslate } from '../api/client';
import { applyPhrases, phrasesFor } from './phrases';
import type { InclusiveOptions } from '../types';

type TextSnapshot = {
    kind: 'text';
    node: Text;
    original: string;
};

type AttributeSnapshot = {
    kind: 'attr';
    element: Element;
    name: string;
    original: string;
};

type TitleSnapshot = {
    kind: 'title';
    original: string;
};

type Snapshot = TextSnapshot | AttributeSnapshot | TitleSnapshot;

const snapshots: Snapshot[] = [];
const SKIP_HOST =
    'script, style, noscript, textarea, code, pre, kbd, samp, template, svg, math, [hidden], [aria-hidden="true"]';
const TRANSLATABLE_ATTRS = [
    'alt',
    'title',
    'placeholder',
    'aria-label',
    'aria-placeholder',
    'aria-roledescription',
    'aria-valuetext',
] as const;
const ATTR_SELECTOR = [
    '[alt]',
    '[title]',
    '[placeholder]',
    '[aria-label]',
    '[aria-placeholder]',
    '[aria-roledescription]',
    '[aria-valuetext]',
    'input[type="submit"]',
    'input[type="button"]',
    'input[type="reset"]',
].join(', ');
const BATCH_SEPARATOR = '\n<<<UGACCESS>>>\n';
const MAX_BATCH_CHARS = 4500;
const MAX_CONCURRENT_BATCHES = 1;
const MAX_UNIQUE_STRINGS = 600;
const CACHE_PERSIST_CHARS = 400_000;
const htmlLangByCode: Record<string, string> = {
    en: 'en',
    eng: 'en',
    lug: 'lg',
    swa: 'sw',
    kin: 'rw',
};

const translationCache = new Map<string, string>();
const hydratedPersistKeys = new Set<string>();

let originalHtmlLang: string | null = null;

export function resetTranslationCache(): void {
    translationCache.clear();
    hydratedPersistKeys.clear();
}

export function restorePageText(): void {
    snapshots.forEach((snapshot) => applySnapshot(snapshot, snapshot.original));
    snapshots.length = 0;

    if (originalHtmlLang !== null) {
        document.documentElement.lang = originalHtmlLang;
        originalHtmlLang = null;
    }
}

export async function translatePage(
    options: InclusiveOptions,
    targetLanguage: string,
    signal?: AbortSignal,
    onApplied?: () => void,
): Promise<boolean> {
    restorePageText();

    if (targetLanguage === 'en' || targetLanguage === 'eng') {
        return true;
    }

    if (signal?.aborted) {
        return false;
    }

    captureHtmlLang();
    collectSnapshots();
    hydrateCache(options.applicationId, targetLanguage);

    if (snapshots.length === 0) {
        setHtmlLang(targetLanguage);

        return true;
    }

    const { visible, rest } = uniqueOriginals(snapshots);
    const translations = new Map<string, string>();
    let notified = false;

    const applyMapped = (mapped: Map<string, string>): void => {
        mapped.forEach((translated, original) => {
            if (translated.trim() === original.trim()) {
                return;
            }

            translations.set(original, translated);
        });

        if (!applyTranslations(mapped)) {
            return;
        }

        setHtmlLang(targetLanguage);

        if (!notified) {
            notified = true;
            onApplied?.();
        }
    };

    const visibleCached = partitionCached(
        options.applicationId,
        targetLanguage,
        visible,
    );
    const restCached = partitionCached(
        options.applicationId,
        targetLanguage,
        rest,
    );

    if (visibleCached.hits.size > 0) {
        applyMapped(visibleCached.hits);
    }

    if (restCached.hits.size > 0) {
        applyMapped(restCached.hits);
    }

    await translateChunks(
        options,
        chunkStrings(visibleCached.missing, MAX_BATCH_CHARS),
        targetLanguage,
        signal,
        applyMapped,
    );

    if (!signal?.aborted) {
        await translateChunks(
            options,
            chunkStrings(restCached.missing, MAX_BATCH_CHARS),
            targetLanguage,
            signal,
            applyMapped,
        );
    }

    if (signal?.aborted) {
        return translations.size > 0;
    }

    if (translations.size === 0) {
        restorePageText();

        return false;
    }

    setHtmlLang(targetLanguage);

    return true;
}

export function ensureSnapshots(): void {
    captureHtmlLang();

    if (snapshots.length === 0) {
        collectSnapshots();
    }
}

export function overlayLocalPhrases(language: string): boolean {
    const phrases = phrasesFor(language);

    if (phrases === null || snapshots.length === 0) {
        return false;
    }

    let changed = false;

    snapshots.forEach((snapshot) => {
        const current = liveValue(snapshot);

        if (normalizeSpace(current) !== normalizeSpace(snapshot.original)) {
            return;
        }

        const next = applyPhrases(snapshot.original, phrases);

        if (next === snapshot.original) {
            return;
        }

        changed = true;
        applySnapshot(snapshot, next);
    });

    if (changed) {
        setHtmlLang(language);
    }

    return changed;
}

function liveValue(snapshot: Snapshot): string {
    if (snapshot.kind === 'text') {
        return snapshot.node.nodeValue ?? '';
    }

    if (snapshot.kind === 'attr') {
        return snapshot.element.getAttribute(snapshot.name) ?? '';
    }

    return document.title;
}

function normalizeSpace(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
}

async function translateChunks(
    options: InclusiveOptions,
    chunks: string[][],
    targetLanguage: string,
    signal: AbortSignal | undefined,
    applyMapped: (mapped: Map<string, string>) => void,
): Promise<void> {
    await mapPool(
        chunks,
        MAX_CONCURRENT_BATCHES,
        async (batch) => {
            if (signal?.aborted) {
                return;
            }

            const mapped = await translateBatch(
                options,
                batch,
                targetLanguage,
                signal,
            );

            if (mapped === null) {
                return;
            }

            applyMapped(mapped);
        },
        signal,
    );
}

async function translateBatch(
    options: InclusiveOptions,
    batch: string[],
    targetLanguage: string,
    signal?: AbortSignal,
    depth = 0,
): Promise<Map<string, string> | null> {
    const mapped = new Map<string, string>();
    const missing: string[] = [];

    batch.forEach((original) => {
        const cached = cachedTranslation(
            options.applicationId,
            targetLanguage,
            original,
        );

        if (cached !== undefined) {
            mapped.set(original, cached);

            return;
        }

        missing.push(original);
    });

    if (missing.length === 0) {
        return mapped;
    }

    if (signal?.aborted) {
        return mapped.size > 0 ? mapped : null;
    }

    const fetched = await requestBatch(
        options,
        missing,
        targetLanguage,
        signal,
        depth,
    );

    if (fetched === null) {
        return mapped.size > 0 ? mapped : null;
    }

    fetched.forEach((translated, original) => {
        rememberTranslation(
            options.applicationId,
            targetLanguage,
            original,
            translated,
        );
        mapped.set(original, translated);
    });

    persistCache(options.applicationId, targetLanguage);

    return mapped;
}

async function requestBatch(
    options: InclusiveOptions,
    batch: string[],
    targetLanguage: string,
    signal: AbortSignal | undefined,
    depth: number,
): Promise<Map<string, string> | null> {
    if (batch.length === 1) {
        const translated = await translateString(
            options,
            batch[0],
            targetLanguage,
            signal,
        );

        if (translated === null) {
            return null;
        }

        return new Map([[batch[0], translated]]);
    }

    const joined = batch.join(BATCH_SEPARATOR);
    const translated = await requestTranslate(
        options,
        joined,
        'en',
        targetLanguage,
        signal,
    );

    if (translated !== null) {
        const parts = translated.split(BATCH_SEPARATOR);

        if (parts.length === batch.length) {
            return new Map(
                batch.map((original, index) => [
                    original,
                    parts[index] ?? original,
                ]),
            );
        }
    }

    if (batch.length <= 2) {
        return translateEach(options, batch, targetLanguage, signal);
    }

    if (depth >= 1) {
        return null;
    }

    const mid = Math.ceil(batch.length / 2);
    const [left, right] = await Promise.all([
        requestBatch(
            options,
            batch.slice(0, mid),
            targetLanguage,
            signal,
            depth + 1,
        ),
        requestBatch(
            options,
            batch.slice(mid),
            targetLanguage,
            signal,
            depth + 1,
        ),
    ]);

    const merged = new Map<string, string>();
    left?.forEach((value, key) => merged.set(key, value));
    right?.forEach((value, key) => merged.set(key, value));

    return merged.size > 0 ? merged : null;
}

async function translateEach(
    options: InclusiveOptions,
    batch: string[],
    targetLanguage: string,
    signal?: AbortSignal,
): Promise<Map<string, string> | null> {
    const mapped = new Map<string, string>();

    for (const original of batch) {
        if (signal?.aborted) {
            break;
        }

        const translated = await translateString(
            options,
            original,
            targetLanguage,
            signal,
        );

        if (translated === null) {
            continue;
        }

        mapped.set(original, translated);
    }

    return mapped.size > 0 ? mapped : null;
}

async function translateString(
    options: InclusiveOptions,
    text: string,
    targetLanguage: string,
    signal?: AbortSignal,
): Promise<string | null> {
    if (text.length <= MAX_BATCH_CHARS) {
        return requestTranslate(options, text, 'en', targetLanguage, signal);
    }

    const pieces: string[] = [];

    for (const piece of splitOversized(text, MAX_BATCH_CHARS)) {
        if (signal?.aborted) {
            return null;
        }

        const translated = await requestTranslate(
            options,
            piece,
            'en',
            targetLanguage,
            signal,
        );

        if (translated === null) {
            return null;
        }

        pieces.push(translated);
    }

    return pieces.join(' ');
}

function collectSnapshots(): void {
    collectTextNodes();
    collectAttributes();
    collectTitle();
}

function collectTextNodes(): void {
    const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode(node) {
                const parent = node.parentElement;

                if (!parent || isUntranslatableHost(parent)) {
                    return NodeFilter.FILTER_REJECT;
                }

                if (!isTranslatableText(node.nodeValue)) {
                    return NodeFilter.FILTER_REJECT;
                }

                return NodeFilter.FILTER_ACCEPT;
            },
        },
    );

    while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        snapshots.push({
            kind: 'text',
            node,
            original: node.nodeValue ?? '',
        });
    }
}

function collectAttributes(): void {
    document.body.querySelectorAll(ATTR_SELECTOR).forEach((element) => {
        if (isUntranslatableHost(element)) {
            return;
        }

        TRANSLATABLE_ATTRS.forEach((name) => {
            const original = element.getAttribute(name);

            if (!isTranslatableText(original)) {
                return;
            }

            snapshots.push({
                kind: 'attr',
                element,
                name,
                original,
            });
        });

        if (
            element instanceof HTMLInputElement &&
            (element.type === 'submit' ||
                element.type === 'button' ||
                element.type === 'reset') &&
            isTranslatableText(element.value) &&
            element.getAttribute('value') !== null
        ) {
            snapshots.push({
                kind: 'attr',
                element,
                name: 'value',
                original: element.value,
            });
        }
    });
}

function collectTitle(): void {
    if (!isTranslatableText(document.title)) {
        return;
    }

    snapshots.push({
        kind: 'title',
        original: document.title,
    });
}

function uniqueOriginals(items: Snapshot[]): {
    visible: string[];
    rest: string[];
} {
    const visible: string[] = [];
    const rest: string[] = [];
    const seen = new Set<string>();

    items.forEach((item) => {
        const text = item.original.trim();

        if (text === '' || seen.has(text)) {
            return;
        }

        seen.add(text);

        if (isSnapshotVisible(item)) {
            visible.push(text);
        } else {
            rest.push(text);
        }
    });

    const combined = [...visible, ...rest].slice(0, MAX_UNIQUE_STRINGS);

    return {
        visible: combined.slice(0, visible.length),
        rest: combined.slice(visible.length),
    };
}

function chunkStrings(items: string[], maxChars: number): string[][] {
    const chunks: string[][] = [];
    let current: string[] = [];
    let size = 0;

    items.forEach((item) => {
        if (item.includes(BATCH_SEPARATOR.trim())) {
            if (current.length > 0) {
                chunks.push(current);
                current = [];
                size = 0;
            }

            chunks.push([item]);

            return;
        }

        const extra =
            item.length + (current.length === 0 ? 0 : BATCH_SEPARATOR.length);

        if (current.length > 0 && size + extra > maxChars) {
            chunks.push(current);
            current = [];
            size = 0;
        }

        current.push(item);
        size += extra;
    });

    if (current.length > 0) {
        chunks.push(current);
    }

    return chunks;
}

function splitOversized(text: string, maxChars: number): string[] {
    if (text.length <= maxChars) {
        return [text];
    }

    const parts: string[] = [];
    let remaining = text;

    while (remaining.length > maxChars) {
        let cut = remaining.lastIndexOf('. ', maxChars);

        if (cut < maxChars / 3) {
            cut = remaining.lastIndexOf(' ', maxChars);
        }

        if (cut < 1) {
            cut = maxChars;
        }

        parts.push(remaining.slice(0, cut).trim());
        remaining = remaining.slice(cut).trim();
    }

    if (remaining !== '') {
        parts.push(remaining);
    }

    return parts;
}

function applyTranslations(translations: Map<string, string>): boolean {
    let applied = false;

    snapshots.forEach((snapshot) => {
        const translated = translations.get(snapshot.original.trim());

        if (translated === undefined) {
            return;
        }

        applySnapshot(
            snapshot,
            replaceTrimmed(
                snapshot.original,
                snapshot.original.trim(),
                translated,
            ),
        );
        applied = true;
    });

    return applied;
}

function applySnapshot(snapshot: Snapshot, value: string): void {
    if (snapshot.kind === 'text') {
        if (snapshot.node.parentNode) {
            snapshot.node.nodeValue = value;
        }

        return;
    }

    if (snapshot.kind === 'attr') {
        snapshot.element.setAttribute(snapshot.name, value);

        return;
    }

    document.title = value;
}

function replaceTrimmed(
    original: string,
    trimmed: string,
    translated: string,
): string {
    const start = original.indexOf(trimmed);

    if (start < 0) {
        return translated;
    }

    return (
        original.slice(0, start) +
        translated +
        original.slice(start + trimmed.length)
    );
}

function isUntranslatableHost(node: Element): boolean {
    return isWidgetNode(node) || node.closest(SKIP_HOST) !== null;
}

function isWidgetNode(node: Element): boolean {
    return (
        node.closest(
            '#inclusive-widget, #inclusive-skip-link, #inclusive-access-link',
        ) !== null
    );
}

function isSnapshotVisible(snapshot: Snapshot): boolean {
    if (snapshot.kind === 'title') {
        return true;
    }

    const element =
        snapshot.kind === 'text'
            ? snapshot.node.parentElement
            : snapshot.element;

    if (!element) {
        return true;
    }

    const rect = element.getBoundingClientRect();

    if (rect.width === 0 && rect.height === 0) {
        return true;
    }

    const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;

    return (
        rect.bottom > 0 &&
        rect.top < viewportHeight &&
        rect.right > 0 &&
        rect.left < viewportWidth
    );
}

function isTranslatableText(value: string | null | undefined): value is string {
    const text = value?.trim() ?? '';

    return text.length >= 2 && /[\p{L}]/u.test(text);
}

function captureHtmlLang(): void {
    if (originalHtmlLang === null) {
        originalHtmlLang = document.documentElement.lang || 'en';
    }
}

function setHtmlLang(language: string): void {
    document.documentElement.lang = htmlLangByCode[language] ?? language;
}

function partitionCached(
    applicationId: string,
    language: string,
    texts: string[],
): { hits: Map<string, string>; missing: string[] } {
    const hits = new Map<string, string>();
    const missing: string[] = [];

    texts.forEach((text) => {
        const cached = cachedTranslation(applicationId, language, text);

        if (cached !== undefined) {
            hits.set(text, cached);

            return;
        }

        missing.push(text);
    });

    return { hits, missing };
}

function cachedTranslation(
    applicationId: string,
    language: string,
    text: string,
): string | undefined {
    return translationCache.get(memoryKey(applicationId, language, text));
}

function rememberTranslation(
    applicationId: string,
    language: string,
    text: string,
    translated: string,
): void {
    translationCache.set(memoryKey(applicationId, language, text), translated);
}

function memoryKey(
    applicationId: string,
    language: string,
    text: string,
): string {
    return `${applicationId}\n${language}\n${text}`;
}

function persistStorageKey(applicationId: string, language: string): string {
    return `inclusive:${applicationId}:tx:${language}`;
}

function hydrateCache(applicationId: string, language: string): void {
    const key = persistStorageKey(applicationId, language);

    if (hydratedPersistKeys.has(key)) {
        return;
    }

    hydratedPersistKeys.add(key);

    const raw = readPersistedCache(key);

    if (!raw) {
        return;
    }

    try {
        const parsed = JSON.parse(raw) as Record<string, string>;

        Object.entries(parsed).forEach(([text, translated]) => {
            translationCache.set(
                memoryKey(applicationId, language, text),
                translated,
            );
        });
    } catch {
        // Stored JSON may be invalid.
    }
}

function persistCache(applicationId: string, language: string): void {
    const prefix = `${applicationId}\n${language}\n`;
    const payload: Record<string, string> = {};
    let chars = 0;

    translationCache.forEach((translated, key) => {
        if (!key.startsWith(prefix) || chars > CACHE_PERSIST_CHARS) {
            return;
        }

        const text = key.slice(prefix.length);
        chars += text.length + translated.length;
        payload[text] = translated;
    });

    writePersistedCache(
        persistStorageKey(applicationId, language),
        JSON.stringify(payload),
    );
}

function readPersistedCache(key: string): string | null {
    try {
        return localStorage.getItem(key) ?? sessionStorage.getItem(key);
    } catch {
        try {
            return sessionStorage.getItem(key);
        } catch {
            return null;
        }
    }
}

function writePersistedCache(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);

        return;
    } catch {
        // Fall through to sessionStorage.
    }

    try {
        sessionStorage.setItem(key, value);
    } catch {
        // Quota or private-mode restrictions should not block translation.
    }
}

async function mapPool<T>(
    items: T[],
    limit: number,
    fn: (item: T) => Promise<void>,
    signal?: AbortSignal,
): Promise<void> {
    if (items.length === 0) {
        return;
    }

    let next = 0;

    const worker = async (): Promise<void> => {
        while (next < items.length) {
            if (signal?.aborted) {
                return;
            }

            const index = next;
            next += 1;
            await fn(items[index]);
        }
    };

    await Promise.all(
        Array.from(
            { length: Math.min(limit, items.length) },
            () => worker(),
        ),
    );
}
